import React, { useState, useEffect } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { useVsiAppContext } from "../stores/VsiApp";
import styles from "../utils/themeStyles";
import { genericColors } from "../utils/chartUtils";
import { withErrorBoundary } from "../boundaries/withErrorBoundary";
import GeneralErrorBoundary from "../boundaries/GeneralErrorBoundary";

require("highcharts/indicators/indicators")(Highcharts);
require("highcharts/indicators/pivot-points")(Highcharts);
require("highcharts/indicators/macd")(Highcharts);
require("highcharts/modules/exporting")(Highcharts);
require("highcharts/modules/map")(Highcharts);

Highcharts.setOptions({
    lang: {
        thousandsSep: ','
    }
});

function tickByStepAngle(seriesLength) {
    // tick different angle based on how many of them and size of screen.
    const screenFactor = window.innerWidth / 1600;
    if (seriesLength > 5 * screenFactor && seriesLength <= 10 * screenFactor) {
        return -45;
    } else if (seriesLength > 10 * screenFactor) {
        return -90;
    } else {
        return 0;
    }
}

function VerticalBarChart({ categories, data, series, title = "", height, includeLegend, axisMaxMin, xTitle, yTitle1, yTitle2, colors }) {
	const { theme, isTabletScreen } = useVsiAppContext();
	const basicTextStyle = {
		color: styles[theme].textColor,
		fontFamily: styles[theme].fontFamily,
	};

	const updateSeries = (series, data) => {
		if (series) return series;
		else if (data)
			return [
				{
                    data: data.map((datum, i) => ({
                        y: datum,
                        color: colors?.[i] || genericColors?.[i] || genericColors?.[i - data.length + 1]
                    })),
				},
			];
		return [];
	};

	const [chartOptions, setChartOptions] = useState(() => {
		return {
            colors: colors || genericColors,
            credits: {
              enabled: false,
            },
			title: {
				text: title,
				style: basicTextStyle,
			},
            mapNavigation: {
              enabled: false
            },
            chart: {
                alignThresholds: true,
                type: 'column',
				height: height || 500,
				backgroundColor: styles[theme].cardBgColor,
				color: styles[theme].textColor,
                marginTop: 50
            },
			legend: {
				itemStyle: basicTextStyle,
				align: "center",
				enabled: includeLegend
			},
            xAxis: {
                categories: categories,
				labels: {
					style: basicTextStyle,
					step: 1
				},
                title: {
                    text: xTitle,
                    style: {
                      ...basicTextStyle,
                      fontWeight: '600'
                    }
                }
            },
            rangeSelector: {
                inputEnabled: true
            },
            yAxis: [
                {
                    title: {
                        useHTML: true,
                        text: yTitle1,
                        style: {
                          ...basicTextStyle,
                          fontWeight: '600'
                        }
                    },
                    labels: {
                        style: basicTextStyle,
                        enabled: true
                    },
                    max: axisMaxMin ? axisMaxMin.axis1Max : null,
                    min: axisMaxMin ? axisMaxMin.axis1Min : null
                },
                {
                    title: {
                        useHTML: true,
                        text: yTitle2
                    },
                    labels: {
                        style: basicTextStyle,
                        enabled: true
                    },
                    opposite: true,
                    max: axisMaxMin ? axisMaxMin.axis2Max : null,
                    min: axisMaxMin ? axisMaxMin.axis2Min : null
                }
            ],
            tooltip: {
                pointFormat: '{series.name}: <b>{point.y}</b>',
	            backgroundColor: styles[theme].altBgColor,
	            style: basicTextStyle
            },
            plotOptions: {
                column: {
                    pointPadding: 0.2,
                    borderWidth: 0,
                    dataLabels: {
                        enabled: true,
                        color: styles[theme].textColor,
                        style: {
                            textOutline: "none"
                        }
                    }
                }
            },
            series: updateSeries(series, data),
		};
	});

	useEffect(() => {
		setChartOptions(() => {
			return {
                colors: colors || genericColors,
                credits: {
                  enabled: false,
                },
                title: {
                    text: title,
                    style: basicTextStyle,
                },
                mapNavigation: {
                  enabled: false
                },
                chart: {
                    alignThresholds: true,
                    // alignTicks: true,
                    type: 'column',
                    height: height || 500,
                    backgroundColor: styles[theme].cardBgColor,
                    color: styles[theme].textColor,
                    marginTop: 50
                },
                legend: {
                    itemStyle: basicTextStyle,
                    align: "center",
                    enabled: includeLegend ? true : false
                },
                xAxis: {
                    categories: categories,
                    labels: {
                        style: basicTextStyle,
                        step: 1,
                        rotation: tickByStepAngle(series?.[0]?.data?.length || 0)
                    },
                    title: {
                        text: xTitle,
                        style: {
                          ...basicTextStyle,
                          fontWeight: '600'
                        }
                    }
                },
                rangeSelector: {
                    inputEnabled: true
                },
                yAxis: [
                    {
                        title: {
                            useHTML: true,
                            text: yTitle1,
                            style: {
                              ...basicTextStyle,
                              fontWeight: '600'
                            }
                        },
                        labels: {
                            style: basicTextStyle,
                            enabled: true
                        },
                        max: axisMaxMin ? axisMaxMin.axis1Max : null,
                        min: axisMaxMin ? axisMaxMin.axis1Min : null
                    },
                    {
                        title: {
                            useHTML: true,
                            text: yTitle2
                        },
                        labels: {
                            style: basicTextStyle,
                            enabled: true
                        },
                        opposite: true,
                        max: axisMaxMin ? axisMaxMin.axis2Max : null,
                        min: axisMaxMin ? axisMaxMin.axis2Min : null
                    }
                ],
                tooltip: {
                    pointFormat: '{series.name}: <b>{point.y}</b>',
                    backgroundColor: styles[theme].altBgColor,
                    style: basicTextStyle
                },
                plotOptions: {
                    column: {
                        pointPadding: 0.2,
                        borderWidth: 0,
                        dataLabels: {
                            enabled: true,
                            color: styles[theme].textColor,
                            style: {
                                textOutline: "none"
                            }
                        }
                    }
                },
                series: updateSeries(series, data),
            };
		});
		// eslint-disable-next-line
	}, [categories, series, data, theme, isTabletScreen, colors]);

	return <HighchartsReact highcharts={Highcharts} options={chartOptions}/>;
}

VerticalBarChart.displayName = "VerticalBarChart"

export default withErrorBoundary(VerticalBarChart, GeneralErrorBoundary);
